import { ColumnWrapper } from '@components/BookingDetails/BookingDetails.styles';
import {
  LuxAccordion,
  LuxDivider,
  LuxModal,
  LuxModalHeader,
  LuxTabsGroup,
  LuxTextField,
  LuxTypography,
  Stack,
  UserDoctorIcon,
  useTheme,
} from '@gemini/fe-ds-core';
import useDoctors from '@hooks/useDoctors';
import useGetAppointments from '@hooks/useGetAppointments';
import useGetPaymentMethods from '@hooks/useGetPaymentMethods';
import {
  OnlineBookingStore,
  useOnlineBookingStore,
} from '@store/onlineBookingStore';
import { GeneralInfoDTO } from '@utils/interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CustMessContainer,
  DoctorAvatarInfoContainer,
  DoctorInfoContainer,
  ElementsList,
  InfoRowContainer,
  ListElement,
  ModalContentWrapper,
  MoreInfoAccordionContent,
} from './MoreInformationModal.styles';
import useGetInsuranceConfiguration from '@hooks/useGetInsuranceConfiguration';
import DataGeneralTable from '@components/DataGeneralTable/DataGeneralTable';
import {
  MobileBreakpoint,
  TabletBreakpoint,
} from '@utils/consts/breakpoints.consts';
import dayjs, { Dayjs } from 'dayjs';
import { useFocusTrap } from '@hooks/useFocusTrap';
import {
  formatGeneralAddress,
  phoneLink,
} from '@components/BookingDetails/utils';

interface MoreInfosModalProps {
  generalData?: GeneralInfoDTO;
  open: boolean;
  onClose: () => void;
  fromViewDoctors?: boolean;
  onlineBookingStore?: OnlineBookingStore;
}

const MoreInformationModal = ({
  generalData,
  open,
  onClose,
  fromViewDoctors = false,
  onlineBookingStore,
}: MoreInfosModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  useFocusTrap({ isOpen: open, onClose, selector: '#LuxDrawerResizable' });

  const examServices = useOnlineBookingStore(
    state => state.generalInfo?.examService,
  );
  const practiceInformation =
    useOnlineBookingStore(state => state.generalConfig?.practiceInformation) ||
    onlineBookingStore?.generalConfig?.practiceInformation;
  const practiceId =
    useOnlineBookingStore(state => state.practiceId) ||
    onlineBookingStore?.practiceId ||
    '';
  const showDoctors =
    useOnlineBookingStore(
      state => state.scheduleConfig?.showDoctorDetailsForWebScheduling,
    ) || onlineBookingStore?.scheduleConfig?.showDoctorDetailsForWebScheduling;
  const organizationId =
    useOnlineBookingStore(state => state.generalInfo?.organizationId) ||
    onlineBookingStore?.generalInfo?.organizationId;

  const brandName = useOnlineBookingStore(state => state.brandName);

  const { data: acceptedInsurances } = useGetInsuranceConfiguration({
    organizationId: brandName ? practiceId : organizationId,
  });
  const { data: appointments } = useGetAppointments({
    practiceIdValue: practiceId,
  });
  const { data: payments } = useGetPaymentMethods({ practiceId });
  const { data: doctors } = useDoctors({
    practiceId,
    enabled: showDoctors,
  });

  const [breakpoint, setBreakpoint] = useState<'mobile' | 'desktop'>(
    window.innerWidth > MobileBreakpoint ? 'desktop' : 'mobile',
  );
  const [isBottomModal, setIsBottomModal] = useState<boolean>(
    window.innerWidth > TabletBreakpoint,
  );

  const fees = useMemo(() => {
    if (practiceInformation?.fees) return practiceInformation.fees;
    return undefined;
  }, [practiceInformation?.fees]);

  const payMethods = useMemo(() => {
    return payments && payments.method_list
      ? payments.method_list.split(',')
      : [];
  }, [payments]);
  const days = useMemo(
    () => [
      {
        label: t('moreInfosModal.days.mon'),
        id: 'MON',
      },
      {
        label: t('moreInfosModal.days.tue'),
        id: 'TUE',
      },
      {
        label: t('moreInfosModal.days.wed'),
        id: 'WED',
      },
      {
        label: t('moreInfosModal.days.thu'),
        id: 'THU',
      },
      {
        label: t('moreInfosModal.days.fri'),
        id: 'FRI',
      },
      {
        label: t('moreInfosModal.days.sat'),
        id: 'SAT',
      },
      {
        label: t('moreInfosModal.days.sun'),
        id: 'SUN',
      },
    ],
    [t],
  );

  const getDayJsTime = useCallback((hour: string) => {
    if (hour.indexOf(':') === -1) return undefined;

    const splitOpeningTime = hour.split(':');

    if (splitOpeningTime.length === 0) return undefined;

    const hourOpeningTime = parseInt(splitOpeningTime[0]);
    const minuteOpeningTime = parseInt(
      splitOpeningTime.length > 2 ? splitOpeningTime[1] : '0',
    );

    if (isNaN(hourOpeningTime) || isNaN(minuteOpeningTime)) return undefined;

    return dayjs()
      .set('hour', hourOpeningTime)
      .set('minute', minuteOpeningTime);
  }, []);

  const practiceHours = useMemo(() => {
    const slots: Map<string, Array<Dayjs>> = new Map();
    if (days && Array.isArray(generalData?.hours)) {
      // get All hours
      generalData.hours.forEach(hour => {
        if (Array.isArray(hour.day)) {
          // for each hour get opening time and closing time
          const openingTime = getDayJsTime(hour.openingTime);
          const closingTime = getDayJsTime(hour.closingTime);
          if (openingTime && closingTime) {
            // for each day add opening time and closing time and sort it
            hour.day.forEach(day => {
              const slot = slots.get(day);
              if (slot) {
                slot.push(openingTime);
                slot.push(closingTime);
                if (slot.length > 2) {
                  // sort only if more than 2
                  slot.sort((a, b) => a.valueOf() - b.valueOf());
                }
              } else {
                slots.set(day, [openingTime, closingTime]);
              }
            });
          }
        }
      });
    }
    return slots;
  }, [days, generalData, t]);
  const [selectedTab, setSelectedTab] = useState<any>();

  const sections = useMemo(() => {
    const tempSec = [];
    if (
      practiceInformation?.showExamServices &&
      ((examServices && examServices.length > 0) ||
        (appointments && appointments.length > 0))
    ) {
      tempSec.push({
        index: tempSec.length,
        content: t('moreInfosModal.examServices'),
        id: 'exams',
      });
    }
    if (
      (practiceInformation?.showPaymentOptions &&
        payMethods &&
        payMethods.length > 0) ||
      (practiceInformation?.showAcceptedInsurances &&
        acceptedInsurances &&
        acceptedInsurances?.length > 0) ||
      (practiceInformation?.showFees && !!fees)
    ) {
      tempSec.push({
        index: tempSec.length,
        content: t('moreInfosModal.paymentInfo'),
        id: 'payments',
      });
    }
    if (showDoctors && doctors && doctors.length > 0) {
      tempSec.push({
        index: tempSec.length,
        content: t('moreInfosModal.doctorInfo'),
        id: 'doctors',
      });
    }
    return tempSec;
  }, [
    practiceInformation,
    examServices,
    t,
    acceptedInsurances,
    doctors,
    payMethods,
    fees,
    appointments,
    showDoctors,
  ]);

  useEffect(() => {
    if (sections) {
      if (fromViewDoctors)
        setSelectedTab(sections.find(sec => sec.id === 'doctors'));
      else setSelectedTab(sections[0]);
    }
  }, [sections, fromViewDoctors]);

  window.addEventListener('resize', () => {
    if (breakpoint === 'mobile' && window.innerWidth > MobileBreakpoint)
      setBreakpoint('desktop');
    if (breakpoint === 'desktop' && window.innerWidth <= MobileBreakpoint)
      setBreakpoint('mobile');
    if (window.innerWidth > TabletBreakpoint) {
      setIsBottomModal(true);
    } else {
      setIsBottomModal(false);
    }
  });

  return (
    <>
      {open && (
        <LuxModal
          type={isBottomModal ? 'right' : 'bottom'}
          open={open}
          minWidthPerc={60}
          maxHeightPerc={85}
          onClose={onClose}
          resizable
          header={
            <LuxModalHeader
              title={
                <ColumnWrapper>
                  <LuxTypography
                    variant="heading3"
                    sx={{
                      fontSize: `${theme.palette.LUX.obTokens.textH2MobSize} !important`,
                      lineHeight: `${theme.palette.LUX.obTokens.textH2MobLineHeight} !important`,
                      textTransform: 'uppercase !important',
                    }}
                  >
                    {generalData?.name}
                  </LuxTypography>
                  <LuxTypography variant="textMdRegular">
                    {formatGeneralAddress(generalData, !!brandName)}
                  </LuxTypography>
                  <LuxTypography
                    variant="textMdRegular"
                    fontWeight={breakpoint === 'mobile' ? '700' : ''}
                  >
                    {breakpoint === 'mobile'
                      ? phoneLink(generalData?.phoneNumber)
                      : generalData?.phoneNumber}
                  </LuxTypography>
                </ColumnWrapper>
              }
              darkMode={false}
              enableCloseIcon
              onClose={onClose}
            />
          }
        >
          <ModalContentWrapper>
            {practiceInformation?.showPracticeHours &&
              practiceHours.size > 0 && (
                <>
                  <LuxTypography variant="heading4">
                    {t('moreInfosModal.hours')}
                  </LuxTypography>
                  {breakpoint === 'desktop' ? (
                    <DataGeneralTable
                      rows={[
                        [
                          ...days.map(d => (
                            <LuxTypography key={d.id} variant="textMedium">
                              {d.label}
                            </LuxTypography>
                          )),
                        ],
                        [
                          ...days.map(
                            d => {
                              const hours = practiceHours.get(d.id);
                              if (hours) {
                                return (
                                  <Stack
                                    direction="column"
                                    key={`hours-${d.id}`}
                                    gap={'0.5rem'}
                                    style={{
                                      borderRadius: 'unset',
                                      backgroundColor: 'unset',
                                      border: 'unset',
                                    }}
                                  >
                                    <LuxTypography variant="textXs">
                                      {hours[0].format('hh:mm A')}
                                    </LuxTypography>
                                    <LuxTypography variant="textXs">
                                      {hours[1].format('hh:mm A')}
                                    </LuxTypography>
                                    {hours.length > 2 && (
                                      <>
                                        <LuxDivider />
                                        <LuxTypography variant="textXs">
                                          {hours[2].format('hh:mm A')}
                                        </LuxTypography>
                                        <LuxTypography variant="textXs">
                                          {hours[3].format('hh:mm A')}
                                        </LuxTypography>
                                      </>
                                    )}
                                  </Stack>
                                );
                              }

                              return t('moreInfosModal.closed');
                            },
                            // practiceHours.find(
                            //   ph =>
                            //     d.id.toLowerCase() === ph.id.toLowerCase(),
                            // )?.hour,
                          ),
                        ],
                      ]}
                      rowFill="even"
                      columnsTemplate="repeat(7, 1fr)"
                    />
                  ) : (
                    <DataGeneralTable
                      rows={days.map(d => {
                        const hours = practiceHours.get(d.id);
                        let hoursText = t('moreInfosModal.closed');
                        if (hours) {
                          hoursText = `${hours[0].format('hh:mm A')} - ${hours[1].format('hh:mm A')}`;
                          if (hours.length > 2) {
                            hoursText += ` / ${hours[2].format('hh:mm A')} - ${hours[3].format('hh:mm A')}`;
                          }
                        }
                        return [
                          <LuxTypography key={d.id} variant="textMedium">
                            {d.label}
                          </LuxTypography>,
                          hoursText,
                        ];
                      })}
                      rowFill="odd"
                      columnsTemplate="1fr 5fr"
                    />
                  )}
                </>
              )}
            {sections.length > 0 && (
              <LuxTabsGroup
                tabs={sections}
                onSelect={index => {
                  setSelectedTab(
                    sections.find(sec => sec.index === index) ?? sections[0],
                  );
                }}
                showIcon={false}
                selected={selectedTab?.index ?? 0}
                disableScrollable
                noLabel
                sx={{
                  ...(breakpoint === 'mobile' && {
                    '& .lux-tabs-group': {
                      height: 'unset',
                      '& button': {
                        flex: '1 1 0',
                        height: '100%',
                      },
                    },
                  }),
                }}
              />
            )}
            {selectedTab?.id === 'exams' && (
              <LuxAccordion
                title={
                  <LuxTypography variant="heading4" padding={'0.5rem 0'}>
                    {t('moreInfosModal.examAccordion')}
                  </LuxTypography>
                }
                fullWidthContent
                defaultExpanded
              >
                <MoreInfoAccordionContent>
                  <LuxTypography variant="textMdRegular">
                    {t('moreInfosModal.examDisclaimer')}
                  </LuxTypography>
                  <LuxTypography variant="textMdRegular">
                    <ElementsList isMobile={breakpoint === 'mobile'}>
                      {examServices && examServices.length > 0
                        ? // Show examServices first when present
                          examServices.map(service => (
                            <ListElement key={service}>
                              <LuxTypography variant="textMdRegular">
                                {service}
                              </LuxTypography>
                            </ListElement>
                          ))
                        : appointments && appointments.length > 0
                          ? // Otherwise show appointments
                            appointments.map(a => (
                              <ListElement key={a.id}>
                                <LuxTypography variant="textMdRegular">
                                  {a.title}
                                </LuxTypography>
                              </ListElement>
                            ))
                          : null}
                    </ElementsList>
                  </LuxTypography>
                </MoreInfoAccordionContent>
              </LuxAccordion>
            )}
            {selectedTab?.id === 'payments' && (
              <>
                {practiceInformation?.showPaymentOptions &&
                  payMethods &&
                  payMethods.length > 0 && (
                    <LuxAccordion
                      title={
                        <LuxTypography variant="heading4" padding={'0.5rem 0'}>
                          {t('moreInfosModal.paymentAccordion')}
                        </LuxTypography>
                      }
                      fullWidthContent
                      defaultExpanded
                    >
                      <MoreInfoAccordionContent>
                        <ElementsList isMobile={breakpoint === 'mobile'}>
                          {payMethods.map(pm => (
                            <ListElement key={pm}>
                              <LuxTypography variant="textMdRegular">
                                {pm}
                              </LuxTypography>
                            </ListElement>
                          ))}
                        </ElementsList>
                      </MoreInfoAccordionContent>
                    </LuxAccordion>
                  )}
                {practiceInformation?.showAcceptedInsurances &&
                  acceptedInsurances &&
                  acceptedInsurances.length > 0 && (
                    <LuxAccordion
                      title={
                        <LuxTypography variant="heading4" padding={'0.5rem 0'}>
                          {brandName
                            ? t('moreInfosModal.acceptedInsurances')
                            : t('moreInfosModal.acceptedInsurances2')}
                        </LuxTypography>
                      }
                      fullWidthContent
                      defaultExpanded
                    >
                      <MoreInfoAccordionContent>
                        <ElementsList isMobile={breakpoint === 'mobile'}>
                          {acceptedInsurances.map(
                            ins =>
                              ins.name !== 'Other' && (
                                <ListElement key={ins.insuranceId}>
                                  <LuxTypography variant="textMdRegular">
                                    {ins.name}
                                  </LuxTypography>
                                </ListElement>
                              ),
                          )}
                        </ElementsList>
                      </MoreInfoAccordionContent>
                    </LuxAccordion>
                  )}
                {practiceInformation?.showFees && fees && (
                  <LuxAccordion
                    title={
                      <LuxTypography variant="heading4" padding={'0.5rem 0'}>
                        {t('moreInfosModal.fees')}
                      </LuxTypography>
                    }
                    fullWidthContent
                    defaultExpanded
                  >
                    <MoreInfoAccordionContent>
                      <LuxTypography variant="textMdRegular">
                        {fees}
                      </LuxTypography>
                    </MoreInfoAccordionContent>
                  </LuxAccordion>
                )}
              </>
            )}
            {selectedTab?.id === 'doctors' &&
              doctors &&
              doctors?.map(doc => (
                <LuxAccordion
                  title={
                    <LuxTypography variant="heading4" padding={'0.5rem 0'}>
                      {doc.title}
                    </LuxTypography>
                  }
                  key={doc.doctorId}
                  fullWidthContent
                  defaultExpanded
                >
                  <MoreInfoAccordionContent>
                    <DoctorAvatarInfoContainer>
                      {doc.avatar ? (
                        <div
                          style={{
                            backgroundImage: `url(${doc.avatar})`,
                            backgroundSize:
                              breakpoint === 'mobile' ? 'contain' : 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            width: breakpoint === 'mobile' ? '100%' : '9rem',
                            height: '9rem',
                            borderRadius: '0.5rem',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor:
                              theme.palette.LUX.obTokens.actionColorBgBrand,
                            borderRadius: '0.5rem',
                            width: `${breakpoint === 'mobile' ? '100%' : '9rem'}`,
                            height: '9rem',
                          }}
                        >
                          <UserDoctorIcon
                            width="7rem"
                            height="7rem"
                            color="white"
                          />
                        </div>
                      )}
                      <DoctorInfoContainer>
                        {doc.items.languages && (
                          <InfoRowContainer>
                            <LuxTypography
                              color={
                                theme.palette.LUX.obTokens.textColorBaseDefault
                              }
                            >
                              {t('moreInfosModal.doctorData.lang')}
                            </LuxTypography>
                            <LuxTypography>
                              {doc.items.languages?.join(', ')}
                            </LuxTypography>
                          </InfoRowContainer>
                        )}
                        {doc.items.gender && (
                          <InfoRowContainer>
                            <LuxTypography
                              color={
                                theme.palette.LUX.obTokens.textColorBaseDefault
                              }
                            >
                              {t('moreInfosModal.doctorData.gender')}
                            </LuxTypography>
                            <LuxTypography>{doc.items.gender}</LuxTypography>
                          </InfoRowContainer>
                        )}
                        {doc.items.biography && (
                          <InfoRowContainer>
                            <LuxTypography
                              color={
                                theme.palette.LUX.obTokens.textColorBaseDefault
                              }
                            >
                              {t('moreInfosModal.doctorData.bio')}
                            </LuxTypography>
                            <LuxTypography>{doc.items.biography}</LuxTypography>
                          </InfoRowContainer>
                        )}
                        {doc.items.specialty && (
                          <InfoRowContainer>
                            <LuxTypography
                              color={
                                theme.palette.LUX.obTokens.textColorBaseDefault
                              }
                            >
                              {t('moreInfosModal.doctorData.spec')}
                            </LuxTypography>
                            <LuxTypography>
                              {doc.items.specialty.map(spec => spec).join(', ')}
                            </LuxTypography>
                          </InfoRowContainer>
                        )}
                        {doc.items.education && (
                          <InfoRowContainer>
                            <LuxTypography
                              color={
                                theme.palette.LUX.obTokens.textColorBaseDefault
                              }
                            >
                              {t('moreInfosModal.doctorData.education')}
                            </LuxTypography>
                            <LuxTypography>
                              {doc.items.education.map(edu => edu).join(', ')}
                            </LuxTypography>
                          </InfoRowContainer>
                        )}
                      </DoctorInfoContainer>
                    </DoctorAvatarInfoContainer>
                    {doc?.customMessage && doc?.customMessage.length > 0 && (
                      <CustMessContainer>
                        <LuxTypography variant="textMedium">
                          {t('moreInfosModal.doctorData.custMess')}
                        </LuxTypography>
                        <LuxTextField
                          multiline
                          minRows={3}
                          maxRows={3}
                          noLabel
                          noHelper
                          defaultValue={doc.customMessage}
                          InputProps={{
                            readOnly: true,
                          }}
                        />
                      </CustMessContainer>
                    )}
                  </MoreInfoAccordionContent>
                </LuxAccordion>
              ))}
          </ModalContentWrapper>
        </LuxModal>
      )}
    </>
  );
};

export default MoreInformationModal;
